import Wrapper from '../assets/wrappers/LandingPage';
import { Link } from 'react-router-dom';
import {
  Briefcase,
  ChartLineUp,
  BellRinging,
  CheckCircle,
  Target,
  ShieldCheck,
  ArrowRight,
  Star,
  Trophy,
  ClipboardText,
  Sparkle,
  Buildings,
} from '@phosphor-icons/react';

const MOCK_JOBS = [
  {
    initial: 'G',
    initBg: '#4285F4',
    company: 'Google',
    role: 'Product Designer',
    time: '2 days ago',
    status: 'Interview',
    statusBg: '#EEF2FF',
    statusColor: '#6366F1',
  },
  {
    initial: 'S',
    initBg: '#1DB954',
    company: 'Spotify',
    role: 'UX Researcher',
    time: '4 days ago',
    status: 'Pending',
    statusBg: '#FEF3C7',
    statusColor: '#D97706',
  },
  {
    initial: 'F',
    initBg: '#A259FF',
    company: 'Figma',
    role: 'UI Designer',
    time: '1 week ago',
    status: 'Offer',
    statusBg: '#D1FAE5',
    statusColor: '#059669',
  },
  {
    initial: 'N',
    initBg: '#1a1a1a',
    company: 'Notion',
    role: 'Design Lead',
    time: '2 weeks ago',
    status: 'Declined',
    statusBg: '#FEE2E2',
    statusColor: '#DC2626',
  },
];

const FEATURES = [
  {
    icon: <Briefcase size={20} weight="duotone" />,
    color: 'indigo',
    title: 'Application Tracker',
    desc: 'Log every job with company, role, date, type, and location. Never lose an application again.',
  },
  {
    icon: <ChartLineUp size={20} weight="duotone" />,
    color: 'emerald',
    title: 'Visual Analytics',
    desc: 'Charts and stats that show where your applications stand and how your week is trending.',
  },
  {
    icon: <CheckCircle size={20} weight="duotone" />,
    color: 'amber',
    title: 'Status Pipeline',
    desc: 'Pending · Interview · Offer · Declined. One-click status updates across every application.',
  },
  {
    icon: <Target size={20} weight="duotone" />,
    color: 'violet',
    title: 'Weekly Goals',
    desc: 'Set application targets, hit them, and build the momentum that gets you hired faster.',
  },
  {
    icon: <ShieldCheck size={20} weight="duotone" />,
    color: 'rose',
    title: 'Secure & Private',
    desc: 'Your data is yours alone. We never sell, share, or expose your job search to anyone.',
  },
  {
    icon: <Buildings size={20} weight="duotone" />,
    color: 'teal',
    title: 'Company Insights',
    desc: 'Track which companies you target and spot patterns in what gets callbacks.',
  },
];

const Landing = () => {
  return (
    <Wrapper>
      {/* ── NAVBAR ── */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-logo">
            <div className="nav-logo-icon">
              <Briefcase size={17} weight="fill" />
            </div>
            <span className="nav-logo-text">Runway</span>
          </div>

          <div className="nav-links">
            <a href="#features" className="nav-link">Features</a>
            <a href="#how-it-works" className="nav-link">How it works</a>
          </div>

          <div className="nav-cta">
            <Link to="/register" className="btn-nav-ghost">Sign in</Link>
            <Link to="/register" className="btn-nav-solid">
              Get started <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">
              <Sparkle size={13} weight="fill" />
              <span>Free job tracking · No credit card</span>
            </div>

            <h1 className="hero-title">
              Track every application.<br />
              <em className="hero-em">Land faster.</em>
            </h1>

            <p className="hero-desc">
              Stop juggling spreadsheets. Runway gives you one clean dashboard to manage every application, follow-up, and offer — from first click to signed contract.
            </p>

            <div className="hero-actions">
              <Link to="/register" className="btn-primary">
                Start tracking free <ArrowRight size={16} weight="bold" />
              </Link>
              <Link to="/register" className="btn-secondary">
                Sign in
              </Link>
            </div>

            <div className="hero-trust">
              <div className="trust-stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={13} weight="fill" />
                ))}
              </div>
              <span className="trust-text">Loved by 10,000+ job seekers</span>
            </div>
          </div>

          {/* ── APP PREVIEW MOCKUP ── */}
          <div className="hero-visual">
            <div className="app-preview">
              <div className="preview-header">
                <div className="preview-header-left">
                  <Briefcase size={15} weight="duotone" />
                  <span>My Applications</span>
                </div>
                <span className="preview-badge">4 active</span>
              </div>

              <div className="preview-list">
                {MOCK_JOBS.map((job, i) => (
                  <div className="preview-job" key={i}>
                    <div
                      className="job-avatar"
                      style={{ background: job.initBg }}
                    >
                      {job.initial}
                    </div>
                    <div className="job-info">
                      <span className="job-company">{job.company}</span>
                      <span className="job-role">{job.role}</span>
                    </div>
                    <div className="job-right">
                      <span className="job-time">{job.time}</span>
                      <span
                        className="job-status"
                        style={{
                          background: job.statusBg,
                          color: job.statusColor,
                        }}
                      >
                        {job.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="preview-footer">
                <ChartLineUp size={13} weight="bold" />
                <span>2 interviews this week · You're on track!</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <section className="stats-bar">
        <div className="stats-container">
          <div className="stats-item">
            <span className="stats-num">10K+</span>
            <span className="stats-label">Active users</span>
          </div>
          <div className="stats-sep" />
          <div className="stats-item">
            <span className="stats-num">250K+</span>
            <span className="stats-label">Jobs tracked</span>
          </div>
          <div className="stats-sep" />
          <div className="stats-item">
            <span className="stats-num">3×</span>
            <span className="stats-label">Faster job search</span>
          </div>
          <div className="stats-sep" />
          <div className="stats-item">
            <span className="stats-num">Free</span>
            <span className="stats-label">Always, forever</span>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="section how-it-works" id="how-it-works">
        <div className="section-container">
          <div className="section-header">
            <span className="section-tag">Simple process</span>
            <h2 className="section-title">Three steps to your offer</h2>
            <p className="section-desc">
              No complicated setup. You'll be tracking your first application in under 60 seconds.
            </p>
          </div>

          <div className="steps-grid">
            <div className="step-card">
              <div className="step-num">01</div>
              <div className="step-icon-wrap">
                <ClipboardText size={26} weight="duotone" />
              </div>
              <h4 className="step-title">Add your applications</h4>
              <p className="step-desc">
                Log the company, role, date applied, and any notes. Quick, structured, and always findable.
              </p>
            </div>

            <div className="step-card step-featured">
              <div className="step-num">02</div>
              <div className="step-icon-wrap step-icon-featured">
                <ChartLineUp size={26} weight="duotone" />
              </div>
              <h4 className="step-title">Update your statuses</h4>
              <p className="step-desc">
                Move jobs between pending, interview, offer, and declined with a single click.
              </p>
            </div>

            <div className="step-card">
              <div className="step-num">03</div>
              <div className="step-icon-wrap">
                <Trophy size={26} weight="duotone" />
              </div>
              <h4 className="step-title">Land the offer</h4>
              <p className="step-desc">
                Stay organized across all your opportunities and walk into every interview fully prepared.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="section features-section" id="features">
        <div className="section-container">
          <div className="section-header">
            <span className="section-tag">Features</span>
            <h2 className="section-title">Built for serious job seekers</h2>
          </div>

          <div className="features-grid">
            {FEATURES.map((f, i) => (
              <div className="feature-card" key={i}>
                <div className={`feature-icon-wrap icon-${f.color}`}>
                  {f.icon}
                </div>
                <h5 className="feature-title">{f.title}</h5>
                <p className="feature-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIAL STRIP ── */}
      <section className="testimonials">
        <div className="section-container">
          <div className="testimonial-grid">
            <div className="testimonial-card">
              <div className="testi-stars">
                {[...Array(5)].map((_, i) => <Star key={i} size={13} weight="fill" />)}
              </div>
              <p className="testi-quote">"I was applying to 20+ jobs a week and losing track of everything. Runway made it dead simple. Got my offer within 6 weeks."</p>
              <div className="testi-author">
                <div className="testi-avatar" style={{ background: '#6366F1' }}>A</div>
                <div>
                  <span className="testi-name">Alex M.</span>
                  <span className="testi-role">Product Designer</span>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testi-stars">
                {[...Array(5)].map((_, i) => <Star key={i} size={13} weight="fill" />)}
              </div>
              <p className="testi-quote">"The status pipeline is incredibly satisfying to use. Moving a card to 'Offer' after weeks of tracking was such a moment."</p>
              <div className="testi-author">
                <div className="testi-avatar" style={{ background: '#059669' }}>J</div>
                <div>
                  <span className="testi-name">Jamie K.</span>
                  <span className="testi-role">Software Engineer</span>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testi-stars">
                {[...Array(5)].map((_, i) => <Star key={i} size={13} weight="fill" />)}
              </div>
              <p className="testi-quote">"Finally something that doesn't need a 30-minute tutorial. I signed up, added my first job in two minutes, and haven't looked back."</p>
              <div className="testi-author">
                <div className="testi-avatar" style={{ background: '#D97706' }}>S</div>
                <div>
                  <span className="testi-name">Sara R.</span>
                  <span className="testi-role">Marketing Manager</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="cta-section">
        <div className="section-container">
          <div className="cta-box">
            <div className="cta-stars">
              {[...Array(5)].map((_, i) => <Star key={i} size={15} weight="fill" />)}
              <span className="cta-rating">4.9 / 5 from 2,400 reviews</span>
            </div>
            <h2 className="cta-title">
              Your next role is closer<br />than you think.
            </h2>
            <p className="cta-desc">
              Take the guesswork out of your job search. Start tracking today — it's completely free.
            </p>
            <Link to="/register" className="btn-cta">
              Create your free account <ArrowRight size={18} weight="bold" />
            </Link>
            <p className="cta-note">Free forever · No credit card · 2-minute setup</p>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-brand">
            <div className="footer-logo-icon">
              <Briefcase size={15} weight="fill" />
            </div>
            <span>Runway</span>
          </div>
          <p className="footer-copy">© 2025 Runway. Built for job seekers.</p>
          <div className="footer-links">
            <Link to="/register" className="footer-link">Sign up</Link>
            <Link to="/register" className="footer-link">Sign in</Link>
          </div>
        </div>
      </footer>
    </Wrapper>
  );
};

export default Landing;
